import { PercentCrop } from "react-image-crop"

/* ------------------------------------------------------------------ *
   Discriminant literal for every variant we know
 * ------------------------------------------------------------------ */

/* ---------------- concrete variants ---------------- */
interface CardPMDetails {
    type: "card";
    brand: string;
    last4: string;
    exp_month: number;
    exp_year: number;
    wallet?: "apple_pay" | "google_pay" | "samsung_pay";
}

interface USBankAccountPMDetails {
    type: "us_bank_account";
    bank_name: string;
    last4: string;
    account_type: string;
}

interface SepaDebitPMDetails {
    type: "sepa_debit";
    bank_code: string;
    branch_code: string;
    last4: string;
}

interface CashAppPMDetails {
    type: "cashapp";
    cashtag: string;
}

interface AmazonPayPMDetails {
    type: "amazon_pay";
}

interface UnknownPMDetails {
    type: "unknown";
    note: string;
}

/* union */
type PaymentMethodDetails =
    | CardPMDetails
    | USBankAccountPMDetails
    | SepaDebitPMDetails
    | CashAppPMDetails
    | AmazonPayPMDetails
    | UnknownPMDetails;

/* ------------------------------------------------------------------ *
   BillingData – revised
 * ------------------------------------------------------------------ */
/* ------------------------------------------------------------------ *
   BillingData – now with cancellation metadata
 * ------------------------------------------------------------------ */
export type BillingData = {

    /* — unchanged fields — */
    is_subscribed: boolean;
    our_plan_id?: string;
    stripe_price_id?: string;
    stripe_subscription_id?: string;
    stripe_customer_id?: string;
    subscribed_at?: string;

    /* subscription-level default PM (card-only struct kept as-is) */
    stripe_subscription_default_payment_method_id?: string;
    stripe_subscription_default_payment_method_card_details?: {
        brand: string;
        last4: string;
        exp_month: number;
        exp_year: number;
    };

    /* NEW: customer-level default PM (any type) */
    stripe_customer_default_payment_method_id?: string;
    stripe_customer_default_payment_method_details?: PaymentMethodDetails;

    /* NEW: cancellation snapshot (mirrors Stripe.Subscription fields) */
    /** When the subscription is scheduled to cancel (Unix epoch seconds). */
    stripe_subscription_cancel_at?: number | null;

    /** When the user actually initiated the cancellation (Unix epoch seconds). */
    stripe_subscription_canceled_at?: number | null;

    /** `true` when Stripe will let the current period finish before cancelling. */
    stripe_subscription_cancel_at_period_end?: boolean;

    /** Stripe’s high-level reason keyword.  */
    stripe_subscription_cancellation_reason?:
    | "cancellation_requested"
    | "payment_failure"
    | "fraud"
    | "subscription_cancellation"
    | "other"
    | string; // fallback for any future values

    /** Customer-supplied feedback such as “too_expensive”. */
    stripe_subscription_cancellation_feedback?:
    | "too_expensive"
    | "missing_features"
    | "switched_provider"
    | "billing_issue"
    | "user_error"
    | string; // fallback

    /* latest invoice snapshot */
    stripe_latest_paid_invoice?: {
        created: number;
        hosted_invoice_url: string;
        id: string;
        invoice_pdf: string;
        number: string;
        period_end: number;
        period_start: number;
        status_transitions: {
            finalized_at: number;
            marked_uncollectible_at: number | null;
            paid_at: number | null;
            voided_at: number | null;
        };
        total: number;
    };
};

export class Account {
    inserted_at: string
    name: string
    unique_identifier: string

    public_data: {
        manually_disabled_free_trial?: boolean
        enable_free_access?: boolean
        downloadable_pdf_last_page_logo_url?: string
        friends_and_relatives_pdf_footer_logo_url?: string
        downloadable_pdf_text_color_hex?: string
        downloadable_pdf_background_color_hex?: string
        downloadable_pdf_final_page_quote?: string
        downloadable_pdf_first_page_quote?: string
        link_to_obituary_listing?: string
        obituary_detail_avatar_url_xpath_selector?: string // maybe belongs in a full model, but there's not much time
        email_follow_up_enabled?: boolean
        email_signature_enabled?: boolean
        email_signature?: string
    }

    last_service_at?: string
    last_signature_at?: string
    billing_data: BillingData
    email_address?: string

    belongs_to_agency?: Agency

    is_demo_account?: boolean

    constructor(data: any) {
        this.inserted_at = data.inserted_at
        this.name = data.name
        this.unique_identifier = data.unique_identifier
        this.public_data = data.public_data
        this.last_service_at = data.last_service_at
        this.last_signature_at = data.last_signature_at
        this.billing_data = data.billing_data
        this.email_address = data.email_address
        this.belongs_to_agency = data.belongs_to_agency ? new Agency(data.belongs_to_agency) : undefined;
        this.is_demo_account = data.is_demo_account || false;
    }
}

export class User {
    inserted_at: string
    updated_at: string

    confirmed_at: string
    unique_identifier: string
    email: string
    name: string
    name_initials: string
    is_superuser: boolean
    is_super_super_user: boolean
    is_agency_admin?: boolean
    belongs_to_agency?: {
        unique_identifier: string
        name: string
        payment_out_of_date?: boolean
    } | null

    public_data: {
        name: string
    }

    constructor(data: any) {
        this.inserted_at = data.inserted_at
        this.updated_at = data.updated_at
        this.confirmed_at = data.confirmed_at
        this.unique_identifier = data.unique_identifier
        this.email = data.email
        this.name = data.name
        this.name_initials = data.name_initials
        this.public_data = data.public_data
        this.is_superuser = data.is_superuser
        this.is_super_super_user = data.is_super_super_user
        this.is_agency_admin = data.is_agency_admin || false
        this.belongs_to_agency = data.belongs_to_agency || null
    }
}

export class UserAccountMembership {
    unique_identifier: string
    inserted_at: string
    updated_at: string
    account: Account
    user: User

    constructor(data: any) {
        this.unique_identifier = data.unique_identifier
        this.inserted_at = data.inserted_at
        this.updated_at = data.updated_at
        this.account = new Account(data.account)
        this.user = new User(data.user)
    }
}


export class ServiceStartAndStopLogItem {
    inserted_at: string
    updated_at: string

    constructor(data: any) {
        this.inserted_at = data.inserted_at
        this.updated_at = data.updated_at
    }
}

export class ServicePublicData {
    deceased_person: {
        avatar_url: string
        birth_date: string
        death_date: string
        full_name: string
        preferred_name: string
    }
    ipad_templates?: {
        portrait_url: string
        landscape_url: string
    }
    latest_digital_download_pdf_url?: string

    advanced_planning_check_text?: string | null
    advanced_planning_check_is_hidden?: boolean | null
    advanced_planning_check_default_checked?: boolean | null
    mobile_advanced_planning_label_header?: string | null
    mobile_advanced_planning_question?: string | null
    current_language?: string

    constructor(data: any) {
        this.deceased_person = data.deceased_person
        this.ipad_templates = data.ipad_templates
        this.latest_digital_download_pdf_url = data.latest_digital_download_pdf_url
        // TODO: both advanced_planning_check_text can end up being null if they were once set but then unset
        // maybe we need to start tracking services changing?
        this.advanced_planning_check_text = data.advanced_planning_check_text
        this.advanced_planning_check_is_hidden = data.advanced_planning_check_is_hidden
        this.advanced_planning_check_default_checked = data.advanced_planning_check_default_checked
        this.mobile_advanced_planning_label_header = data.mobile_advanced_planning_label_header
        this.mobile_advanced_planning_question = data.mobile_advanced_planning_question
        this.current_language = data.current_language
    }
}

export class Service {
    inserted_at: string
    updated_at: string

    unique_identifier: string

    public_data: ServicePublicData

    account: Account

    is_active: boolean

    last_start_log_item: ServiceStartAndStopLogItem | null
    last_stop_log_item: ServiceStartAndStopLogItem | null

    qr_code_sign_url: string

    constructor(data: any) {
        this.inserted_at = data.inserted_at
        this.updated_at = data.updated_at
        this.unique_identifier = data.unique_identifier
        this.public_data = new ServicePublicData(data.public_data)
        this.account = new Account(data.account)
        this.is_active = data.is_active
        this.last_start_log_item = data.last_start_log_item ? new ServiceStartAndStopLogItem(data.last_start_log_item) : null
        this.last_stop_log_item = data.last_stop_log_item ? new ServiceStartAndStopLogItem(data.last_stop_log_item) : null
        this.qr_code_sign_url = data.qr_code_sign_url
    }

    getDirectLink(): string {
        return `${this.qr_code_sign_url}`
    }
}

export class ServiceFamilyMember {
    inserted_at: string
    updated_at: string

    unique_identifier: string

    service: Service

    full_name: string
    email_address: string
    phone_number: string
    relationship_to_deceased?: string

    public_data: any

    constructor(data: any) {
        this.inserted_at = data.inserted_at
        this.updated_at = data.updated_at
        this.unique_identifier = data.unique_identifier
        this.service = new Service(data.service)
        this.full_name = data.full_name
        this.email_address = data.email_address
        this.phone_number = data.phone_number
        this.relationship_to_deceased = data.relationship_to_deceased
        this.public_data = data.public_data
    }
}

class SignatureTranscriptionGeneratedJson {
    name: string
    relationship?: string
    email?: string
    phone_number?: string
    mailing_address_line_1?: string
    mailing_address_line_2?: string
    receive_information_on_advance_planning?: boolean | null
    keep_signature_private?: boolean | null

    constructor(data: any) {
        this.name = data.name
        this.relationship = data.relationship || undefined
        this.email = data.email || undefined
        this.phone_number = data.phone_number || undefined
        this.mailing_address_line_1 = data.mailing_address_line_1 || undefined
        this.mailing_address_line_2 = data.mailing_address_line_2 || undefined
        this.receive_information_on_advance_planning = typeof data.receive_information_on_advance_planning === 'boolean' ? data.receive_information_on_advance_planning : null
        this.keep_signature_private = typeof data.keep_signature_private === 'boolean' ? data.keep_signature_private : null
    }
}

class SignatureTranscription {
    latest_generated_json_source: string
    generated_json: SignatureTranscriptionGeneratedJson

    constructor(data: any) {
        this.latest_generated_json_source = data.latest_generated_json_source || '(unknown)'
        this.generated_json = new SignatureTranscriptionGeneratedJson(data.generated_json || {})
    }
}

class ServiceSignaturePublicData {
    based_on_simple_signature: {
        email_address: string
        full_name: string
        mailing_address_line1: string
        mailing_address_line2: string
        phone_number: string
        interested_in_planning_ahead: boolean
    }
    raw_signature_url: string
    screen_capture_url: string
    latest_signature_transcription?: SignatureTranscription
    receive_information_on_advance_planning?: boolean
    
    // For signatures created from email links
    created_from_email_link?: boolean
    signer_info?: {
        name: string
        email?: string
        phone?: string
        relationship?: string
        address?: string
    }

    mogul_bridge_sync?: {
        last_synced_at: string
        event_unique_identifier: string
        location_unique_identifier: string
        event_check_in_record_unique_identifier: string
    }

    constructor(data: any) {
        this.based_on_simple_signature = data.based_on_simple_signature
        this.raw_signature_url = data.raw_signature_url
        this.screen_capture_url = data.screen_capture_url
        this.latest_signature_transcription = data.latest_signature_transcription ? new SignatureTranscription(data.latest_signature_transcription) : undefined
        this.receive_information_on_advance_planning = data.receive_information_on_advance_planning // this is redundant but MVP mode
        this.created_from_email_link = data.created_from_email_link
        this.signer_info = data.signer_info
        this.mogul_bridge_sync = data.mogul_bridge_sync || undefined
    }
}

export class ServiceSignature {
    inserted_at: string
    updated_at: string

    unique_identifier: string
    signature_type?: 'ipad' | 'qr_code' | 'email'  // Explicit signature type

    service: Service

    created_by_ipad: SimpleIpad | null

    public_data: ServiceSignaturePublicData

    leave_signature_out_of_printed_pdf_and_books: boolean

    constructor(data: any) {
        this.inserted_at = data.inserted_at
        this.updated_at = data.updated_at
        this.unique_identifier = data.unique_identifier
        this.signature_type = data.signature_type
        this.service = new Service(data.service)
        this.created_by_ipad = data.created_by_ipad ? new SimpleIpad(data.created_by_ipad) : null
        this.public_data = new ServiceSignaturePublicData(data.public_data)
        this.leave_signature_out_of_printed_pdf_and_books = data.leave_signature_out_of_printed_pdf_and_books
    }
}

class PhysicalBookPublicData {
    status: 'processing' | 'ready' | 'failed' | 'ordered' // failed never technically happens right now
    preview_cover_url?: string
    output_urls?: {
        final_url_for_cover: string
        final_url_for_interior: string
    }
    lulu_order_id?: number

    constructor(data: any) {
        this.status = data.status
        this.preview_cover_url = data.preview_cover_url
        this.output_urls = data.output_urls ? {
            final_url_for_cover: data.output_urls.final_url_for_cover,
            final_url_for_interior: data.output_urls.final_url_for_interior
        } : undefined
        this.lulu_order_id = data.lulu_order_id
    }
}

export class PhysicalBook {
    inserted_at: string
    updated_at: string

    unique_identifier: string

    service: Service

    public_data: PhysicalBookPublicData

    constructor(data: any) {
        this.inserted_at = data.inserted_at
        this.updated_at = data.updated_at
        this.unique_identifier = data.unique_identifier
        this.service = new Service(data.service)
        this.public_data = new PhysicalBookPublicData(data.public_data)
    }
}

export class SimpleIpad {
    inserted_at: string
    updated_at: string

    unique_identifier: string

    public_data: {
        ipad_name: string
    }

    private_data: {
        ipad_pin: string
    }

    ipad_is_enabled: boolean
    last_seen_online_at: string
    has_been_setup: boolean

    constructor(data: any) {
        this.inserted_at = data.inserted_at
        this.updated_at = data.updated_at
        this.unique_identifier = data.unique_identifier
        this.public_data = data.public_data
        this.private_data = data.private_data
        this.ipad_is_enabled = data.ipad_is_enabled
        this.last_seen_online_at = data.last_seen_online_at
        this.has_been_setup = data.has_been_setup
    }
}

export class Ipad {
    inserted_at: string
    updated_at: string

    unique_identifier: string

    public_data: {
        ipad_name: string
    }
    private_data: {
        ipad_pin: string
    } // only available in management app

    account: Account
    active_service?: Service

    constructor(data: any) {
        this.inserted_at = data.inserted_at
        this.updated_at = data.updated_at
        this.unique_identifier = data.unique_identifier
        this.public_data = data.public_data
        this.private_data = data.private_data
        this.account = new Account(data.account)
        this.active_service = data.active_service ? new Service(data.active_service) : null
    }
}

export class IpadSetupToken {
    inserted_at: string
    updated_at: string

    unique_identifier: string
    short_unique_identifier: string

    ipad_setup_token_is_enabled: boolean

    qr_code_url: string

    ipad: Ipad

    constructor(data: any) {
        this.inserted_at = data.inserted_at
        this.updated_at = data.updated_at
        this.unique_identifier = data.unique_identifier
        this.short_unique_identifier = data.short_unique_identifier
        this.ipad_setup_token_is_enabled = data.ipad_setup_token_is_enabled
        this.qr_code_url = data.qr_code_url
        this.ipad = new Ipad(data.ipad)
    }
}

export class LuluShippingOption {
    business_only: boolean
    carrier_service_name: string
    cost_excl_tax: number
    currency: string
    home_only: boolean
    id: number
    is_active: boolean
    level: string
    max_delivery_date: string
    max_dispatch_date: string
    min_delivery_date: string
    min_dispatch_date: string
    postbox_ok: boolean
    total_days_max: number
    total_days_min: number
    traceable: boolean
    transit_time: number

    constructor(data: any) {
        this.business_only = data.business_only
        this.carrier_service_name = data.carrier_service_name
        this.cost_excl_tax = data.cost_excl_tax
        this.currency = data.currency
        this.home_only = data.home_only
        this.id = data.id
        this.is_active = data.is_active
        this.level = data.level
        this.max_delivery_date = data.max_delivery_date
        this.max_dispatch_date = data.max_dispatch_date
        this.min_delivery_date = data.min_delivery_date
        this.min_dispatch_date = data.min_dispatch_date
        this.postbox_ok = data.postbox_ok
        this.total_days_max = data.total_days_max
        this.total_days_min = data.total_days_min
        this.traceable = data.traceable
        this.transit_time = data.transit_time
    }
}

export class LuluEstimatedCost {
    currency: string
    fulfillment_cost: {
        tax_rate: string
        total_cost_excl_tax: string
        total_cost_incl_tax: string
        total_tax: string
    }
    line_item_costs: {
        cost_excl_discounts: string
        discounts: any[]
        quantity: number
        tax_rate: string
        total_cost_excl_discounts: string
        total_cost_excl_tax: string
        total_cost_incl_tax: string
        total_tax: string
        unit_tier_cost: string
    }[]
    shipping_cost: {
        tax_rate: string
        total_cost_excl_tax: string
        total_cost_incl_tax: string
        total_tax: string
    }
    total_cost_excl_tax: string
    total_cost_incl_tax: string
    total_discount_amount: string
    total_tax: string

    constructor(data: any) {
        this.currency = data.currency
        this.fulfillment_cost = data.fulfillment_cost
        this.line_item_costs = data.line_item_costs
        this.shipping_cost = data.shipping_cost
        this.total_cost_excl_tax = data.total_cost_excl_tax
        this.total_cost_incl_tax = data.total_cost_incl_tax
        this.total_discount_amount = data.total_discount_amount
        this.total_tax = data.total_tax
    }
}

interface CropValueItem {
    percent_values: PercentCrop
    resulting_crop: {
        x: number
        y: number
        width: number
        height: number
    }
    item_is_disabled?: boolean
}

class TemplateCropPublicData {
    crop_values?: {
        printed_name: CropValueItem | null,
        relationship_to_deceased: CropValueItem | null,
        email_address: CropValueItem | null,
        phone_number: CropValueItem | null,
        mailing_address_line_1: CropValueItem | null,
        mailing_address_line_2: CropValueItem | null,
        signature_image: CropValueItem | null,
    } | null

    constructor(data: any) {
        this.crop_values = data.crop_values || null
    }
}

export class TemplateCrop {
    // This maps to ServiceGuestbook.Models.Services.AccountTemplateCrop

    unique_identifier: string
    inserted_at: string
    updated_at: string
    public_data: TemplateCropPublicData
    specific_resolution: string

    // For now this never actually has an account object, but that may change.
    // Keeping it simple for now.
    account: Account | null
    latest_signature: ServiceSignature | null
    signature_count?: number

    constructor(data: any) {
        this.unique_identifier = data.unique_identifier
        this.inserted_at = data.inserted_at
        this.updated_at = data.updated_at
        this.public_data = new TemplateCropPublicData(data.public_data)
        this.specific_resolution = data.specific_resolution
        this.account = data.account ? new Account(data.account) : null
        this.latest_signature = data.latest_signature ? new ServiceSignature(data.latest_signature) : null
        this.signature_count = data.signature_count
    }
}

export class SelectableObituary {
    unique_identifier: string
    inserted_at: string
    updated_at: string
    url: string
    public_data: {
        latest_obituary_detail_extraction?: {
            inserted_at: string
            total_tokens: number
            result: {
                full_name: string
                avatar_url?: string
                date_of_birth?: string
                date_of_death?: string
                preferred_name?: string
            }
        }
    }

    constructor(data: any) {
        this.unique_identifier = data.unique_identifier
        this.inserted_at = data.inserted_at
        this.updated_at = data.updated_at
        this.url = data.url
        this.public_data = data.public_data
    }
}


export class Agency {
    inserted_at: string
    name: string
    unique_identifier: string
    payment_out_of_date: boolean

    public_data: {
    }

    constructor(data: any) {
        this.inserted_at = data.inserted_at
        this.name = data.name
        this.unique_identifier = data.unique_identifier
        this.payment_out_of_date = data.payment_out_of_date || false
        this.public_data = data.public_data
    }
}

export class GeneratedPDF {
    unique_identifier: string
    inserted_at: string
    updated_at: string
    public_data: {
    }

    constructor(data: any) {
        this.unique_identifier = data.unique_identifier
        this.inserted_at = data.inserted_at
        this.updated_at = data.updated_at
        this.public_data = data.public_data
    }
}
/* ------------------------------------------------------------------ *
   The management Export Report (GET /authenticated/management/activity-report):
   activity per calendar month for the accounts the user sees. Counts only.
 * ------------------------------------------------------------------ */
export interface ActivityReportMonth {
    month: string                       // "2026-09"
    to_date: boolean                    // the month holds "now" and counts to now
    accounts_created: number
    accounts_active: number
    users_active: number
    ipads_active: number
    services_created: number
    signatures: number
    signatures_interested_in_planning: number
    total_accounts: number              // running totals at the month's end
    total_ipads: number
    total_services: number
    total_signatures: number
}

export interface ActivityReportAccountRow {
    month: string
    account_unique_id: string
    name: string
    agency_name: string | null
    created_this_month: boolean
    services_created: number
    signatures: number
    signatures_interested_in_planning: number
    ipads_active: number
    services_started_or_stopped: number
    last_signature_at: string | null    // ISO 8601, UTC
}

export interface ActivityReport {
    from: string
    to: string
    generated_at: string                // ISO 8601, UTC
    scope: {
        accounts_counted: number
        demo_accounts_left_out: number
    }
    months: ActivityReportMonth[]
    accounts: ActivityReportAccountRow[]
    definitions: string[]
}
